import { memo, useCallback, useContext } from 'react';
import { round } from 'mathjs';
import SliderDisplay from './SliderDisplay/SliderDisplay';
import MobileTechnologyControllers from './MobileTechnologyController/MobileTechnologyController';
import Slider from './Slider/Slider';
import styles from './Controls.module.css';
import ToolTip from '../Tooltip/Tooltip';
import {
  AllInputDataContext,
  CalculatedDataContext,
  SetSliderValuesContext,
  SliderValuesContext,
} from '../../config';
import { isBrowser, isTablet } from 'react-device-detect';

type Props = {
  incrementAmmount: { incrementBy: number };
};

export const MEControls = memo(({ incrementAmmount }: Props) => {
  const sliderValues = useContext(SliderValuesContext);

  const setSliderValues = useContext(SetSliderValuesContext);

  const { optionsData, technologyData } = useContext(AllInputDataContext);
  const { dataIndex, annualConst, firstRunValues } = useContext(
    CalculatedDataContext
  );

  const updateSliderValues = useCallback(
    (value: number, techIndexNumber: number) => {
      setSliderValues((sliderValues) => {
        const values = [...sliderValues];
        values[techIndexNumber] = value;
        return values;
      });
    },
    [setSliderValues]
  );

  const handleInputChange = useCallback(
    async (value: string | number, techIndexNumber: number) => {
      // `value` arrives as a string from the text input and as a number from
      // the mobile controller; JS coerces it during these numeric checks.
      const numericValue = value as number;
      if (
        numericValue > +technologyData[techIndexNumber].capMax ||
        numericValue < 0 ||
        isNaN(numericValue)
      ) {
        alert(
          `Value entered: "${value}" Please enter a number between 0 and ${technologyData[techIndexNumber].capMax}`
        );
      } else {
        updateSliderValues(value as number, techIndexNumber);
      }
    },
    [technologyData, updateSliderValues]
  );

  return (
    <div className={styles.allSliderControls}>
      <ToolTip
        name='Sliders'
        description='Slide to the left to decommission existing capacity or right to build new plant.'
        color='rgba(255,255,255,0.75)'
        position={[3, 0, 0, -8]}
      />
      {[...Array(optionsData.noOfTechs)].map((x, index) => {
        if (index === dataIndex.blackout) {
          return null;
        }

        return isBrowser || isTablet ? (
          <div
            key={`SliderKey-${index}`}
            id={
              technologyData[index].name.toLowerCase().replace(/\s/g, '') +
              'Slider'
            }
            className={styles.sliderWrapper}
          >
            <label className={styles.sliderLabel}>
              {technologyData[index].name} Capacity
            </label>
            <div className={styles.sliderContainer}>
              <Slider
                techIndexNumber={index}
                value={round(sliderValues[index], 2)}
                originalValue={firstRunValues.originalSliderValues![index]}
                updateSliderValues={updateSliderValues}
              />
              <SliderDisplay
                techIndexNumber={index}
                technologyCost={round(annualConst.annualTechCost[index])}
              />
            </div>
          </div>
        ) : (
          <MobileTechnologyControllers
            value={round(sliderValues[index], 1)}
            setSliderValues={setSliderValues}
            techIndexNumber={index}
            incrementAmmount={incrementAmmount}
            handleInputChange={handleInputChange}
            key={`${index}`}
          />
        );
      })}
    </div>
  );
});
